import * as d3 from "d3";
import { apiEndpoints } from "@config/env";
import { getData } from "./global";
import {
  GP50_MANEUVER_METRIC_COLUMN_KEYS,
  gp50ManeuverColumnHeader,
  isGp50ManeuverHeatmapColumn,
  type Gp50ManeuverType,
} from "./gp50ManeuverCheatSheetColumns";

export type { Gp50ManeuverType };

export interface PerformanceDriversManeuverRow {
  twsBin: number;
  values: Record<string, number | null>;
  recordCount: number | null;
  empty: boolean;
}

function rowValue(row: Record<string, unknown>, key: string): unknown {
  return row[key] ?? row[key.toLowerCase()] ?? row[key.toUpperCase()];
}

/** Match condition_model_utils.configuration_group (first 3 chars, uppercase). */
export function configurationGroup(config: string): string {
  const trimmed = config.trim();
  if (!trimmed) return "UNK";
  return trimmed.slice(0, 3).toUpperCase();
}

export function normalizeManeuverApiRows(raw: unknown[]): Record<string, unknown>[] {
  return raw.map((entry) => {
    const row = (entry && typeof entry === "object" ? entry : {}) as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(row)) {
      out[k.toLowerCase()] = row[k];
    }
    return out;
  });
}

export function aggregateManeuverMetricsForConfigGroup(
  rows: Record<string, unknown>[],
  configGroup: string | undefined,
  metricKeys: readonly string[] = GP50_MANEUVER_METRIC_COLUMN_KEYS
): { values: Record<string, number | null>; recordCount: number | null; empty: boolean } {
  const matching =
    configGroup && configGroup.trim() !== ""
      ? rows.filter((row) => configurationGroup(String(rowValue(row, "config") ?? "")) === configGroup)
      : rows;

  if (matching.length === 0) {
    return { values: {}, recordCount: null, empty: true };
  }

  const values: Record<string, number | null> = {};
  for (const key of metricKeys) {
    let weightSum = 0;
    let valueSum = 0;
    for (const row of matching) {
      const raw = rowValue(row, key);
      const num = typeof raw === "number" ? raw : Number(raw);
      if (!Number.isFinite(num)) continue;
      const countRaw = rowValue(row, "record_count");
      const count = typeof countRaw === "number" ? countRaw : Number(countRaw);
      const weight = Number.isFinite(count) && count > 0 ? count : 1;
      valueSum += num * weight;
      weightSum += weight;
    }
    values[key] = weightSum > 0 ? valueSum / weightSum : null;
  }

  let totalCount = 0;
  for (const row of matching) {
    const countRaw = rowValue(row, "record_count");
    const count = typeof countRaw === "number" ? countRaw : Number(countRaw);
    if (Number.isFinite(count) && count > 0) totalCount += count;
  }

  return {
    values,
    recordCount: totalCount > 0 ? totalCount : null,
    empty: false,
  };
}

export async function fetchManeuverCheatSheetForTws(params: {
  className: string;
  projectId: string;
  maneuverType: Gp50ManeuverType;
  twsBin: number;
}): Promise<Record<string, unknown>[]> {
  const url =
    `${apiEndpoints.app.data}/maneuver-cheat-sheet` +
    `?class_name=${encodeURIComponent(params.className)}` +
    `&project_id=${encodeURIComponent(params.projectId)}` +
    `&group_by=channel` +
    `&maneuver_type=${encodeURIComponent(params.maneuverType)}` +
    `&tws=${encodeURIComponent(params.twsBin)}`;

  const result = await getData(url);
  if (!result?.success) {
    throw new Error(result?.message ?? "Failed to load maneuver cheat sheet data");
  }
  const payload = result.data as { rows?: unknown[] };
  const raw = Array.isArray(payload?.rows) ? payload.rows : [];
  return normalizeManeuverApiRows(raw);
}

export async function fetchPerformanceDriversManeuverTable(params: {
  className: string;
  projectId: string;
  maneuverType: Gp50ManeuverType;
  twsBins: number[];
  configurationGroup?: string;
}): Promise<PerformanceDriversManeuverRow[]> {
  const { className, projectId, maneuverType, twsBins, configurationGroup } = params;
  const rows = await Promise.all(
    twsBins.map(async (twsBin) => {
      try {
        const apiRows = await fetchManeuverCheatSheetForTws({
          className,
          projectId,
          maneuverType,
          twsBin,
        });
        const aggregated = aggregateManeuverMetricsForConfigGroup(apiRows, configurationGroup);
        return {
          twsBin,
          values: aggregated.values,
          recordCount: aggregated.recordCount,
          empty: aggregated.empty,
        };
      } catch {
        return {
          twsBin,
          values: {},
          recordCount: null,
          empty: true,
        };
      }
    })
  );
  return rows;
}

export function maneuverTableColumns(): string[] {
  return [...GP50_MANEUVER_METRIC_COLUMN_KEYS, "record_count"];
}

export function maneuverColumnScales(rows: PerformanceDriversManeuverRow[]): Record<string, [number, number]> {
  const columns = maneuverTableColumns().filter(isGp50ManeuverHeatmapColumn);
  const scales: Record<string, [number, number]> = {};
  for (const col of columns) {
    const values: number[] = [];
    for (const row of rows) {
      if (row.empty) continue;
      const v = row.values[col];
      if (v != null && Number.isFinite(v)) values.push(v);
    }
    if (values.length === 0) continue;
    scales[col] = [d3.min(values) ?? 0, d3.max(values) ?? 0];
  }
  return scales;
}

export function maneuverCellHeatClass(
  value: number | null | undefined,
  column: string,
  scales: Record<string, [number, number]>
): string {
  if (!isGp50ManeuverHeatmapColumn(column)) return "";
  if (value == null || !Number.isFinite(value)) return "performance-drivers-cell-empty";
  const domain = scales[column];
  if (!domain) return "";
  const [min, max] = domain;
  if (min === max) return "c3";
  const scale = d3.scaleLinear().domain([min, max]).range([0, 6]);
  const idx = Math.round(Math.max(0, Math.min(6, scale(value))));
  return `c${idx}`;
}

export function formatManeuverCellValue(column: string, value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  if (column.toLowerCase() === "record_count") return String(Math.round(value));
  const rounded = Math.round(value * 10) / 10;
  return rounded === value ? String(value) : rounded.toFixed(1);
}

export { gp50ManeuverColumnHeader };
